/**
 * External dependencies
 */
import { isEqual, keyBy, sortBy } from 'lodash';
import { EMPTY_ARRAY } from '@nab/utils';
import type { AnyAction } from '@nab/types';

/**
 * Internal dependencies
 */
import { INIT_STATE } from '../config';
import type { State as FullState } from '../types';
import type { SettingsAction as Action } from '../actions/settings';

type State = FullState[ 'settings' ];

export function settings(
	state = INIT_STATE.settings,
	action: AnyAction
): State {
	return actualReducer( state, action as Action ) ?? state;
}

function actualReducer( state: State, action: Action ): State {
	switch ( action.type ) {
		case 'SET_TODAY':
			return {
				...state,
				today: action.today,
			};

		case 'SET_SITE_LANGUAGE':
			return {
				...state,
				language: action.language,
			};

		case 'RECEIVE_MENUS': {
			const newMenus = action.menus.filter(
				( menu ) => ! isEqual( state.menus[ menu.id ], menu )
			);
			if ( ! newMenus.length ) {
				return state;
			}

			return {
				...state,
				menus: {
					...state.menus,
					...keyBy( newMenus, 'id' ),
				},
			};
		}

		case 'RECEIVE_PLUGINS':
			if ( isEqual( state.plugins, action.plugins ) ) {
				return state;
			}

			return {
				...state,
				plugins: action.plugins || EMPTY_ARRAY,
			};

		case 'RECEIVE_TEMPLATE_CONTEXTS':
			if ( isEqual( state.templateContexts, action.templateContexts ) ) {
				return state;
			}

			return {
				...state,
				templateContexts: action.templateContexts,
			};

		case 'RECEIVE_TEMPLATES':
			if ( isEqual( state.templates, action.templates ) ) {
				return state;
			}

			return {
				...state,
				templates: action.templates,
			};

		case 'RECEIVE_THEMES': {
			const newThemes = keyBy( action.themes, 'id' );
			if ( isEqual( state.themes, newThemes ) ) {
				return state;
			}

			return {
				...state,
				themes: newThemes,
			};
		}

		case 'RECEIVE_PLUGIN_SETTINGS':
			if ( isEqual( state.nelio, action.settings ) ) {
				return state;
			}

			return {
				...state,
				nelio: action.settings,
			};

		case 'RECEIVE_ECOMMERCE_SETTINGS': {
			const newEcommerceSettings = {
				...state.ecommerce,
				[ action.ecommerce ]: {
					...action.settings,
					orderStatuses: sortBy(
						action.settings.orderStatuses,
						'label'
					),
				},
			};
			if ( isEqual( state.ecommerce, newEcommerceSettings ) ) {
				return state;
			}

			return {
				...state,
				ecommerce: newEcommerceSettings,
			};
		}

		case 'UPDATE_AI_SETTINGS':
			if ( ! state.nelio.aiSettings ) {
				return state;
			}

			return {
				...state,
				nelio: {
					...state.nelio,
					aiSettings: action.settings,
				},
			};
	}
}
